import React from 'react';
import { Minus, Plus } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface Pricing {
  min: number;
  max: number;
  recommended: number;
}

interface Step6Props {
  pricing: Pricing;
  selectedPrice: number;
  setSelectedPrice: (price: number) => void;
  handleStep6: () => void;
  loading: boolean;
  isGeneratingPricing: boolean;
}

const W = 420;
const H = 180;
const PAD = 34;

const DistributionCurve: React.FC<{ pricing: Pricing; selectedPrice: number }> = ({
  pricing,
  selectedPrice,
}) => {
  const range = Math.max(pricing.max - pricing.min, 0.01);

  const x = (price: number) =>
    PAD + Math.min(Math.max((price - pricing.min) / range, 0), 1) * (W - 2 * PAD);

  const curve = Array.from({ length: 61 }, (_, i) => {
    const price = (i / 60) * range + pricing.min;
    const norm = (price - pricing.recommended) / (range / 4);
    const y = Math.exp(-0.5 * norm * norm);
    return `${x(price)},${H - PAD - y * (H - 2 * PAD)}`;
  }).join(' ');

  const offCentre = selectedPrice > 0 && Math.abs(selectedPrice - pricing.recommended) > 0.001;

  return (
    <div className="ed-panel">
      <h3 className="ed-panel-title">Market distribution</h3>

      <svg
        className="ed-curve"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Price distribution from $${pricing.min.toFixed(2)} to $${pricing.max.toFixed(
          2
        )}, recommended $${pricing.recommended.toFixed(2)}`}
      >
        <polygon
          points={`${PAD},${H - PAD} ${curve} ${W - PAD},${H - PAD}`}
          fill="var(--st-wash-2)"
        />
        <line
          x1={PAD}
          y1={H - PAD}
          x2={W - PAD}
          y2={H - PAD}
          stroke="var(--ed-rule-2)"
          strokeWidth="1"
        />
        <polyline
          points={curve}
          fill="none"
          stroke="var(--st-accent)"
          strokeWidth="2"
          strokeLinecap="round"
        />

        <line
          x1={x(pricing.recommended)}
          y1={PAD - 12}
          x2={x(pricing.recommended)}
          y2={H - PAD}
          stroke="var(--st-accent-text)"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <text
          x={x(pricing.recommended)}
          y={PAD - 18}
          textAnchor="middle"
          fill="var(--st-accent-text)"
          fontSize="9"
          fontWeight="700"
          letterSpacing="1.4"
        >
          SUGGESTED
        </text>

        {offCentre && (
          <>
            <line
              x1={x(selectedPrice)}
              y1={PAD - 12}
              x2={x(selectedPrice)}
              y2={H - PAD}
              stroke="var(--ed-ink)"
              strokeWidth="2"
            />
            <circle cx={x(selectedPrice)} cy={H - PAD} r="4" fill="var(--ed-ink)" />
            <text
              x={x(selectedPrice)}
              y={H - PAD + 22}
              textAnchor="middle"
              fill="var(--ed-ink)"
              fontSize="9"
              fontWeight="700"
              letterSpacing="1.4"
            >
              YOURS
            </text>
          </>
        )}
      </svg>

      <div className="ed-price-legend">
        <div>
          Floor<strong>${pricing.min.toFixed(2)}</strong>
        </div>
        <div>
          Suggested<strong>${pricing.recommended.toFixed(2)}</strong>
        </div>
        <div>
          Ceiling<strong>${pricing.max.toFixed(2)}</strong>
        </div>
      </div>
    </div>
  );
};

const Step6: React.FC<Step6Props> = ({
  pricing,
  selectedPrice,
  setSelectedPrice,
  handleStep6,
  loading,
  isGeneratingPricing,
}) => {
  const isReady = !isGeneratingPricing && !!pricing.recommended;

  const nudge = (delta: number) =>
    setSelectedPrice(Math.round(Math.max(0, selectedPrice + delta) * 100) / 100);

  return (
    <div className="ed-enter">
      <StepHeading
        index={6}
        kicker="Pricing"
        title="Find the number that feels"
        accent="inevitable."
        lede="We read category benchmarks and perceived value to land on a suggestion. Move it if you know something the data doesn't."
      />

      <div className="ed-block">
        <div className="ed-price">
          {isReady ? (
            <DistributionCurve pricing={pricing} selectedPrice={selectedPrice} />
          ) : (
            <div className="ed-skeleton ed-skeleton--tall">Analysing market pricing</div>
          )}

          {isReady ? (
            <div className="ed-panel">
              <h3 className="ed-panel-title">Your price</h3>

              <div className="ed-stepper">
                <button
                  type="button"
                  className="ed-stepper-btn"
                  onClick={() => nudge(-0.5)}
                  aria-label="Decrease price by 50 cents"
                >
                  <Minus size={17} />
                </button>

                <div className="ed-stepper-value">
                  <span className="ed-stepper-currency">$</span>
                  <input
                    type="number"
                    className="ed-stepper-input"
                    value={selectedPrice || ''}
                    onChange={(e) => setSelectedPrice(parseFloat(e.target.value) || 0)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedPrice && !loading) handleStep6();
                    }}
                    aria-label="Product price"
                    {...noAutofill}
                  />
                </div>

                <button
                  type="button"
                  className="ed-stepper-btn"
                  onClick={() => nudge(0.5)}
                  aria-label="Increase price by 50 cents"
                >
                  <Plus size={17} />
                </button>
              </div>

              <p className="ed-hint">Steps of $0.50. Type a figure to set it exactly.</p>
            </div>
          ) : (
            <div className="ed-skeleton ed-skeleton--tall">Preparing controls</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step6;
